import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { Link } from "react-router-dom";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { Download, History, Pencil, Plus, Printer } from "lucide-react";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DatePicker } from "@/components/ui/date-picker";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { useCargosWithDetails } from "@/hooks/useCargos";
import { useAppLocale } from "@/lib/i18n";
import { BADGE_TONES, pickAssortedBadgeClass } from "@/lib/badge-tones";
import { type ExportColumn, exportToCSV } from "@/lib/export";
import { dateInputStringToLocalEndOfDayMs, dateInputStringToLocalStartOfDayMs, formatCurrency, formatDate, formatStatusLabel, timestampToLocalDateInputString } from "@/lib/format";
import { getInventoryStatusBadgeClass } from "@/lib/status-tag-colors";
import { m } from "@/paraglide/messages.js";

import { CargoCreateDialog } from "./cargo-create-dialog";
import { CargoEditDialog } from "./cargo-edit-dialog";

type CargoAuditData = {
  cargoId: string;
  totals: {
    deductedBags: number;
    refundedBags: number;
    netBags: number;
    orderCount: number;
    eventCount: number;
  };
  orders: Array<{
    orderId: string;
    orderNumber: string | null;
    orderStatus: string | null;
    customerName: string | null;
    customerCompany: string | null;
    deductedBags: number;
    refundedBags: number;
    netBags: number;
    lastEventAt: number;
    events: Array<{
      auditLogId: string;
      createdAt: number;
      operation: string;
      source: string;
      quantityChanged: number;
    }>;
  }>;
  events: Array<{
    auditLogId: string;
    createdAt: number;
    orderId: string;
    orderNumber: string | null;
    orderStatus: string | null;
    customerName: string | null;
    customerCompany: string | null;
    operation: string;
    source: string;
    quantityChanged: number;
    beforeAvailableBags: number;
    beforeSoldBags: number;
    beforeWarehouseBags: number;
    afterAvailableBags: number;
    afterSoldBags: number;
    afterWarehouseBags: number;
  }>;
};

const DEFAULT_VISIBLE_STATUSES = [
  "at_origin",
  "upcoming",
  "in_transit",
  "in_spot",
] as const;

const INVENTORY_STATUS_ORDER: Record<string, number> = {
  at_origin: 0,
  upcoming: 1,
  in_transit: 2,
  in_spot: 3,
  sold_out: 4,
  delivered: 5,
  archived: 6,
  unknown: 7,
};

function palletLocationToDisplay(value: unknown): string {
  if (typeof value === "string" && value.trim()) return value;
  return "";
}

function resolveOriginName(cargo: any): string {
  const productOrigin = cargo?.product?.origin;
  if (typeof productOrigin === "string" && productOrigin.trim()) return productOrigin;
  if (typeof productOrigin?.name === "string" && productOrigin.name.trim()) {
    return productOrigin.name;
  }
  if (typeof cargo?.product?.originName === "string" && cargo.product.originName.trim()) {
    return cargo.product.originName;
  }
  if (typeof cargo?.origin === "string" && cargo.origin.trim()) return cargo.origin;
  if (typeof cargo?.country === "string" && cargo.country.trim()) return cargo.country;
  return "";
}

function getStatusSortRank(status: unknown): number {
  const fallbackRank = INVENTORY_STATUS_ORDER.unknown ?? Number.MAX_SAFE_INTEGER;
  if (typeof status !== "string") return fallbackRank;
  return INVENTORY_STATUS_ORDER[status] ?? fallbackRank;
}

function textOrDash(value: unknown): string {
  const text = String(value ?? "").trim();
  return text.length > 0 ? text : "-";
}

function isCargoFeatured(cargo: any): boolean {
  if (cargo?.featured !== undefined) return cargo.featured === true;
  return cargo?.product?.featured === true;
}

function formatBagCount(value: unknown): string {
  const asNumber = Number(value ?? 0);
  if (!Number.isFinite(asNumber)) return "0";
  return asNumber.toLocaleString();
}

function formatAuditDateTime(value: unknown): string {
  const asNumber = Number(value);
  if (!Number.isFinite(asNumber)) return "-";
  return formatDate(asNumber, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }) || "-";
}

function getOrderLookupToken(orderNumber: unknown, orderId: unknown): string {
  const normalizedOrderNumber = String(orderNumber ?? "").trim();
  if (normalizedOrderNumber) return normalizedOrderNumber;
  return String(orderId ?? "").trim();
}

function useColumns(data: any[]) {
  useAppLocale();

  const originOptions = React.useMemo(() => {
    const origins = [...new Set(data.map((cargo) => resolveOriginName(cargo)).filter(Boolean))].sort();
    return origins.map((origin) => ({ label: origin, value: origin }));
  }, [data]);

  const statusFilterOptions = [
    { label: m.status_at_origin(), value: "at_origin" },
    { label: m.status_upcoming(), value: "upcoming" },
    { label: m.status_in_transit(), value: "in_transit" },
    { label: m.status_in_spot(), value: "in_spot" },
    { label: m.status_sold_out(), value: "sold_out" },
    { label: m.status_archived(), value: "archived" },
  ];

  return React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "origin",
        accessorFn: (row) => resolveOriginName(row),
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.origin()} />,
        cell: ({ row }) => {
          const location = resolveOriginName(row.original);
          return location ? (
            <Badge variant="outline" className={pickAssortedBadgeClass(location)}>
              {location}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          );
        },
        meta: {
          label: m.origin(),
          placeholder: m.search_origin_placeholder(),
          variant: "multiSelect",
          options: originOptions,
        },
        filterFn: (row, _columnId, filterValues: string[]) =>
          filterValues.some((value) => value === resolveOriginName(row.original)),
        enableColumnFilter: true,
      },
      {
        id: "ociNumber",
        accessorKey: "ociNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_oci_number_short()} />,
        cell: ({ row }) => {
          const recon = row.original.asOfReconstruction;
          const noHistory =
            recon && (recon.hasAuditData === false || recon.basis === "current-fallback");
          return (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-mono text-xs font-medium">{row.original.ociNumber || "-"}</span>
              {noHistory && (
                <span title={m.cargos_as_of_no_audit_data()} className="inline-flex">
                  <History className="h-3 w-3 text-amber-600" aria-label={m.cargos_as_of_no_audit_data()} />
                </span>
              )}
            </span>
          );
        },
        meta: { label: m.cargos_oci(), placeholder: m.cargos_search_oci_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "aciNumber",
        accessorFn: (row) => row.contractId ?? "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_aci_number_short()} />,
        cell: ({ row }) => <span className="font-mono text-xs font-medium">{row.original.contractId || "-"}</span>,
        meta: { label: m.cargos_aci(), placeholder: m.cargos_search_aci_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "product",
        accessorFn: (row) => row.product?.name ?? "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.product()} />,
        cell: ({ row }) => <span className="font-medium text-sm">{row.original.product?.name ?? "-"}</span>,
        meta: { label: m.product(), placeholder: m.search_product_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "palletLocation",
        accessorFn: (row) => palletLocationToDisplay(row.palletLocation),
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_pallet_location()} />,
        cell: ({ row }) => {
          const palletLocation = palletLocationToDisplay(row.original.palletLocation);
          return palletLocation ? (
            <Badge variant="outline" className={pickAssortedBadgeClass(palletLocation)}>
              {palletLocation}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          );
        },
        meta: {
          label: m.cargos_pallet_location(),
          placeholder: m.cargos_search_pallet_location_placeholder(),
          variant: "text",
        },
        enableColumnFilter: true,
      },
      {
        id: "totalBags",
        accessorKey: "totalBags",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_total()} />,
        cell: ({ row }) => <span>{row.original.totalBags ?? 0}</span>,
        meta: { label: m.cargos_total_bags(), variant: "range" },
        enableColumnFilter: true,
      },
      {
        id: "availableBags",
        accessorKey: "availableBags",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_available()} />,
        cell: ({ row }) => <span className="font-medium">{row.original.availableBags ?? 0}</span>,
        meta: { label: m.cargos_available(), variant: "range" },
        enableColumnFilter: true,
      },
      {
        id: "soldBags",
        accessorKey: "soldBags",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_sold()} />,
        cell: ({ row }) => <span className="text-sm">{row.original.soldBags ?? 0}</span>,
        meta: { label: m.cargos_sold(), variant: "range" },
        enableColumnFilter: true,
      },
      {
        id: "warehouseBags",
        accessorKey: "warehouseBags",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_wh_bags()} />,
        cell: ({ row }) => <span className="text-sm">{row.original.warehouseBags ?? 0}</span>,
        meta: { label: m.cargos_wh_bags(), variant: "range" },
        enableColumnFilter: true,
      },
      {
        id: "pricePerPound",
        accessorKey: "pricePerPound",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_price_per_lb()} />,
        cell: ({ row }) => {
          const price = row.original.pricePerPound;
          return price != null ? <span className="font-medium">{formatCurrency(price)}</span> : <span className="text-muted-foreground">-</span>;
        },
        meta: { label: m.cargos_price(), variant: "number", placeholder: m.cargos_min_price() },
        enableColumnFilter: true,
      },
      {
        id: "diff",
        accessorKey: "diff",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_diff()} />,
        cell: ({ row }) => {
          const diff = row.original.diff;
          return diff != null ? <span className="text-sm">{formatCurrency(diff)}</span> : <span className="text-muted-foreground">-</span>;
        },
        meta: { label: m.cargos_diff(), variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "arrivalDate",
        accessorKey: "arrivalDate",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_arrival()} />,
        cell: ({ row }) => {
          const d = row.original.arrivalDate;
          return d ? <span className="text-sm">{formatDate(d, { month: "short" })}</span> : <span className="text-muted-foreground">-</span>;
        },
        meta: { label: m.cargos_arrival(), variant: "dateRange" },
        enableColumnFilter: true,
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.status()} />,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            <Badge variant="outline" className={getInventoryStatusBadgeClass(row.original.status)}>
              {formatStatusLabel(row.original.status)}
            </Badge>
            {isCargoFeatured(row.original) ? (
              <Badge variant="outline" className={BADGE_TONES.emerald}>{m.status_featured()}</Badge>
            ) : null}
          </div>
        ),
        meta: {
          label: m.status(),
          variant: "multiSelect",
          options: statusFilterOptions,
        },
        sortingFn: (left, right, columnId) =>
          getStatusSortRank(left.getValue(columnId)) - getStatusSortRank(right.getValue(columnId)),
        enableColumnFilter: true,
      },
      {
        id: "archived",
        accessorKey: "archived",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargos_archived()} />,
        cell: ({ row }) => row.original.archived
          ? <Badge variant="outline" className={BADGE_TONES.zinc}>{m.yes()}</Badge>
          : <span className="text-muted-foreground">{m.no()}</span>,
      },
    ],
    [originOptions, statusFilterOptions],
  );
}

function CargoOrderInventoryAuditDialog({
  cargo,
  open,
  onOpenChange,
}: {
  cargo: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  useAppLocale();
  const cargoId = open ? (cargo?._id ?? cargo?.id) : null;
  const auditData = useQuery(
    api.cargos.getCargoOrderInventoryAudit,
    cargoId ? { cargoId: cargoId as any } : "skip",
  ) as CargoAuditData | undefined;

  const productName = textOrDash(cargo?.product?.name);
  const location = textOrDash(cargo?.product?.origin?.name);
  const ociNumber = textOrDash(cargo?.ociNumber);
  const aciNumber = textOrDash(cargo?.contractId);
  const palletLocation = textOrDash(palletLocationToDisplay(cargo?.palletLocation));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-hidden p-0 sm:max-w-6xl">
        <div className="flex h-full flex-col">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>{m.cargos_deduction_audit_title()}</DialogTitle>
            <DialogDescription>
              {m.cargos_deduction_audit_description()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto px-6 py-4">
            <div className="grid gap-2 rounded-md border bg-muted/25 p-3 text-sm md:grid-cols-5">
              <div>
                <p className="text-xs text-muted-foreground">{m.product()}</p>
                <p className="font-medium">{productName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{m.origin()}</p>
                <p className="font-medium">{location}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{m.cargos_oci()}</p>
                <p className="font-mono text-xs font-medium">{ociNumber}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{m.cargos_aci()}</p>
                <p className="font-mono text-xs font-medium">{aciNumber}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{m.cargos_pallet_location()}</p>
                <p className="font-medium">{palletLocation}</p>
              </div>
            </div>

            {auditData === undefined ? (
              <div className="space-y-3">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-44 w-full" />
              </div>
            ) : (auditData.events?.length ?? 0) === 0 ? (
              <p className="rounded-md border px-3 py-8 text-center text-sm text-muted-foreground">
                {m.cargos_no_deduction_history()}
              </p>
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground">{m.cargos_deducted()}</p>
                      <p className="text-2xl font-semibold">{formatBagCount(auditData.totals.deductedBags)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground">{m.cargos_refunded()}</p>
                      <p className="text-2xl font-semibold">{formatBagCount(auditData.totals.refundedBags)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground">{m.cargos_net()}</p>
                      <p className="text-2xl font-semibold">{formatBagCount(auditData.totals.netBags)}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">{m.cargos_orders_section()}</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{m.order()}</TableHead>
                        <TableHead>{m.cargos_customer()}</TableHead>
                        <TableHead>{m.status()}</TableHead>
                        <TableHead className="text-right">{m.cargos_deducted()}</TableHead>
                        <TableHead className="text-right">{m.cargos_refunded()}</TableHead>
                        <TableHead className="text-right">{m.cargos_net()}</TableHead>
                        <TableHead>{m.cargos_last_event()}</TableHead>
                        <TableHead className="text-right">{m.cargos_open()}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditData.orders.map((order) => {
                        const orderLookupToken = getOrderLookupToken(order.orderNumber, order.orderId);
                        const orderLink = orderLookupToken
                          ? `/logistics/orders?orderNumber=${encodeURIComponent(orderLookupToken)}`
                          : "/logistics/orders";
                        const customer = order.customerCompany || order.customerName || "-";
                        return (
                          <TableRow key={order.orderId}>
                            <TableCell className="font-mono text-xs">{textOrDash(order.orderNumber || order.orderId)}</TableCell>
                            <TableCell>{textOrDash(customer)}</TableCell>
                            <TableCell>{textOrDash(formatStatusLabel(order.orderStatus, "-"))}</TableCell>
                            <TableCell className="text-right">{formatBagCount(order.deductedBags)}</TableCell>
                            <TableCell className="text-right">{formatBagCount(order.refundedBags)}</TableCell>
                            <TableCell className="text-right">{formatBagCount(order.netBags)}</TableCell>
                            <TableCell>{formatAuditDateTime(order.lastEventAt)}</TableCell>
                            <TableCell className="text-right">
                              <Button asChild size="sm" variant="ghost">
                                <Link to={orderLink}>{m.view()}</Link>
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">{m.cargos_event_history()}</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{m.cargos_time()}</TableHead>
                        <TableHead>{m.order()}</TableHead>
                        <TableHead>{m.cargos_operation()}</TableHead>
                        <TableHead>{m.cargos_source()}</TableHead>
                        <TableHead className="text-right">{m.quantity_short()}</TableHead>
                        <TableHead className="text-right">{m.cargos_avail_before_after()}</TableHead>
                        <TableHead className="text-right">{m.cargos_sold_before_after()}</TableHead>
                        <TableHead className="text-right">{m.cargos_wh_before_after()}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditData.events.map((event) => (
                        <TableRow key={event.auditLogId}>
                          <TableCell>{formatAuditDateTime(event.createdAt)}</TableCell>
                          <TableCell className="font-mono text-xs">{textOrDash(event.orderNumber || event.orderId)}</TableCell>
                          <TableCell>{textOrDash(formatStatusLabel(event.operation, "-"))}</TableCell>
                          <TableCell>{textOrDash(event.source)}</TableCell>
                          <TableCell className="text-right">{formatBagCount(event.quantityChanged)}</TableCell>
                          <TableCell className="text-right">{`${formatBagCount(event.beforeAvailableBags)} -> ${formatBagCount(event.afterAvailableBags)}`}</TableCell>
                          <TableCell className="text-right">{`${formatBagCount(event.beforeSoldBags)} -> ${formatBagCount(event.afterSoldBags)}`}</TableCell>
                          <TableCell className="text-right">{`${formatBagCount(event.beforeWarehouseBags)} -> ${formatBagCount(event.afterWarehouseBags)}`}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// CSV column layout for the inventory list export. Labels stay English so the
// exported report is stable regardless of the admin's UI locale.
const INVENTORY_EXPORT_COLUMNS: ExportColumn[] = [
  { key: "ociNumber", label: "OCI" },
  { key: "contractId", label: "ACI / Contract" },
  { key: "product", label: "Product" },
  { key: "origin", label: "Origin" },
  { key: "palletLocation", label: "Pallet Location" },
  { key: "totalBags", label: "Total Bags" },
  { key: "availableBags", label: "Available Bags" },
  { key: "soldBags", label: "Sold Bags" },
  { key: "warehouseBags", label: "Warehouse Bags" },
  { key: "pricePerPound", label: "Price / lb", format: (v) => (v == null ? "" : formatCurrency(v)) },
  { key: "status", label: "Status" },
  { key: "archived", label: "Archived" },
];

function buildInventoryExportRows(cargos: any[]) {
  return cargos.map((c) => ({
    ociNumber: c.ociNumber ?? "",
    contractId: c.contractId ?? "",
    product: c.product?.name ?? "",
    origin: resolveOriginName(c),
    palletLocation: palletLocationToDisplay(c.palletLocation),
    totalBags: Number(c.totalBags ?? 0),
    availableBags: Number(c.availableBags ?? 0),
    soldBags: Number(c.soldBags ?? 0),
    warehouseBags: Number(c.warehouseBags ?? 0),
    pricePerPound: c.pricePerPound ?? null,
    status: formatStatusLabel(c.status),
    archived: c.archived ? "Yes" : "No",
  }));
}

const SALES_RANGE_EXPORT_COLUMNS: ExportColumn[] = [
  { key: "ociNumber", label: "OCI" },
  { key: "contractId", label: "ACI / Contract" },
  { key: "productName", label: "Product" },
  { key: "originName", label: "Origin" },
  { key: "deductedBags", label: "Sold" },
  { key: "refundedBags", label: "Returned" },
  { key: "netBags", label: "Net Sold" },
  { key: "orderCount", label: "Orders" },
];

// Self-contained "bags sold over a date range" report. Lives on the Cargos page
// below the inventory list. Mirrors the View Deductions definition of "sold":
// deduct/warehouse_deduct = sold, refund/warehouse_refund = returned.
function InventorySalesRangeCard() {
  useAppLocale();
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");

  const fromMs = dateInputStringToLocalStartOfDayMs(from);
  const toMs = dateInputStringToLocalEndOfDayMs(to);
  const hasBoth = fromMs !== undefined && toMs !== undefined;
  const invalidRange = hasBoth && (fromMs as number) > (toMs as number);
  const isValid = hasBoth && !invalidRange;

  const report = useQuery(
    api.cargos.getInventorySalesByCargo,
    isValid ? { from: fromMs as number, to: toMs as number } : "skip",
  );

  const today = timestampToLocalDateInputString(Date.now());
  const hasRows = !!report && report.rows.length > 0;

  const handleExport = React.useCallback(() => {
    if (!report || report.rows.length === 0) return;
    const rows: Array<Record<string, any>> = report.rows.map((r: any) => ({
      ociNumber: r.ociNumber ?? "",
      contractId: r.contractId ?? "",
      productName: r.productName ?? "",
      originName: r.originName ?? "",
      deductedBags: r.deductedBags,
      refundedBags: r.refundedBags,
      netBags: r.netBags,
      orderCount: r.orderCount,
    }));
    rows.push({
      ociNumber: m.cargos_sales_range_total_row(),
      contractId: "",
      productName: "",
      originName: "",
      deductedBags: report.totals.deductedBags,
      refundedBags: report.totals.refundedBags,
      netBags: report.totals.netBags,
      orderCount: report.totals.orderCount,
    });
    exportToCSV(rows, SALES_RANGE_EXPORT_COLUMNS, `bags-sold-${from}_to_${to}.csv`);
  }, [report, from, to]);

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{m.cargos_sales_range_title()}</CardTitle>
            <CardDescription>{m.cargos_sales_range_description()}</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {m.cargos_sales_range_from()}
              </span>
              <DatePicker value={from} onChange={setFrom} maxDate={today} className="w-[140px]" />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {m.cargos_sales_range_to()}
              </span>
              <DatePicker value={to} onChange={setTo} minDate={from || undefined} maxDate={today} className="w-[140px]" />
            </div>
            <Button size="sm" variant="outline" onClick={handleExport} disabled={!hasRows}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              {m.cargos_export_csv()}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {invalidRange ? (
          <p className="py-6 text-center text-sm text-red-600 dark:text-red-400">
            {m.cargos_sales_range_invalid()}
          </p>
        ) : !isValid ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {m.cargos_sales_range_prompt()}
          </p>
        ) : report === undefined ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : report.rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {m.cargos_sales_range_no_sales()}
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {m.cargos_sales_range_summary({
                net: formatBagCount(report.totals.netBags),
                cargos: report.totals.cargoCount,
                orders: report.totals.orderCount,
                from: formatDate(report.from),
                to: formatDate(report.to),
              })}
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{m.cargos_oci()}</TableHead>
                    <TableHead>{m.cargos_aci()}</TableHead>
                    <TableHead>{m.product()}</TableHead>
                    <TableHead>{m.origin()}</TableHead>
                    <TableHead className="text-right">{m.cargos_sales_range_deducted()}</TableHead>
                    <TableHead className="text-right">{m.cargos_sales_range_refunded()}</TableHead>
                    <TableHead className="text-right">{m.cargos_sales_range_net()}</TableHead>
                    <TableHead className="text-right">{m.cargos_sales_range_orders()}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.rows.map((r: any) => (
                    <TableRow key={r.cargoId}>
                      <TableCell className="font-mono text-xs">{textOrDash(r.ociNumber)}</TableCell>
                      <TableCell className="font-mono text-xs">{textOrDash(r.contractId)}</TableCell>
                      <TableCell className="text-sm font-medium">{textOrDash(r.productName)}</TableCell>
                      <TableCell className="text-sm">{textOrDash(r.originName)}</TableCell>
                      <TableCell className="text-right">{formatBagCount(r.deductedBags)}</TableCell>
                      <TableCell className="text-right">{formatBagCount(r.refundedBags)}</TableCell>
                      <TableCell className="text-right font-medium">{formatBagCount(r.netBags)}</TableCell>
                      <TableCell className="text-right">{formatBagCount(r.orderCount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 font-semibold">
                    <TableCell colSpan={4}>{m.cargos_sales_range_total_row()}</TableCell>
                    <TableCell className="text-right">{formatBagCount(report.totals.deductedBags)}</TableCell>
                    <TableCell className="text-right">{formatBagCount(report.totals.refundedBags)}</TableCell>
                    <TableCell className="text-right">{formatBagCount(report.totals.netBags)}</TableCell>
                    <TableCell className="text-right">{formatBagCount(report.totals.orderCount)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function InventoryCargosPage() {
  useAppLocale();
  const { authUserId } = useAuthIdentity();
  const bulkUpdateCargos = useMutation(api.cargos.bulkUpdateCargos);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [editCargo, setEditCargo] = React.useState<any>(null);
  const [auditCargo, setAuditCargo] = React.useState<any>(null);
  const [asOfDate, setAsOfDate] = React.useState("");

  // `asOfDate` is a yyyy-MM-dd day; reconstruct inventory at the *close* of that
  // day (last ms) so an audit reads "what was on hand at end of <date>".
  // End-of-day is computed via calendar arithmetic so it stays correct on DST days.
  const dayStartMs = dateInputStringToLocalStartOfDayMs(asOfDate);
  const asOfMs = dateInputStringToLocalEndOfDayMs(asOfDate);
  const isHistorical = asOfMs !== undefined;

  const liveCargos = useCargosWithDetails();
  const historicalCargos = useQuery(
    api.admin.getCargosWithDetailsAsOf,
    isHistorical && authUserId ? { authUserId, asOfDate: asOfMs } : "skip",
  );
  const cargos = isHistorical ? historicalCargos : liveCargos;

  const historyCoverage = useQuery(
    api.cargos.getInventoryHistoryCoverage,
    isHistorical ? {} : "skip",
  );
  const changeLog = useQuery(
    api.cargos.getInventoryChangeLog,
    isHistorical && dayStartMs !== undefined && asOfMs !== undefined
      ? { from: dayStartMs, to: asOfMs }
      : "skip",
  );

  const predatesHistory =
    isHistorical &&
    historyCoverage?.earliestAt != null &&
    (asOfMs as number) < historyCoverage.earliestAt;

  const columns = useColumns(cargos ?? []);

  const activeCargos = cargos?.filter((c: any) => !c.archived && c.status !== "sold_out") ?? [];
  const archivedCargos = cargos?.filter((c: any) => c.archived || c.status === "sold_out") ?? [];
  const sameProductCargoIds = React.useMemo(() => {
    const currentCargoId = editCargo?._id ?? editCargo?.id;
    const currentProductId = editCargo?.productId ?? editCargo?.product?._id;

    if (!currentCargoId || !currentProductId || !cargos) {
      return [];
    }

    return cargos
      .filter((cargo: any) => {
        const cargoId = cargo._id ?? cargo.id;
        const productId = cargo.productId ?? cargo.product?._id;
        return cargoId !== currentCargoId && productId === currentProductId;
      })
      .map((cargo: any) => cargo._id ?? cargo.id)
      .filter(Boolean);
  }, [cargos, editCargo]);

  const handleBulkArchive = React.useCallback(
    async (selectedRows: any[], archived: boolean, clearSelection: () => void) => {
      const updates = selectedRows
        .map((row) => row._id ?? row.id)
        .filter(Boolean)
        .map((id) => ({ id, fields: { archived } }));

      if (updates.length === 0) {
        toast.error(m.cargos_no_cargos_selected());
        return;
      }

      try {
        const result = await bulkUpdateCargos({ updates: updates as any[] });
        const successCount = Array.isArray(result)
          ? result.filter((row: any) => row.success).length
          : 0;
        toast.success(
          archived
            ? m.cargos_archived_count({ count: successCount })
            : m.cargos_unarchived_count({ count: successCount }),
        );
        clearSelection();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : m.cargos_update_failed());
      }
    },
    [bulkUpdateCargos],
  );

  const handleExportInventory = React.useCallback(() => {
    if (!cargos || cargos.length === 0) return;
    const rows = buildInventoryExportRows(cargos);
    const suffix =
      isHistorical && asOfDate
        ? `as-of-${asOfDate}`
        : `live-${timestampToLocalDateInputString(Date.now())}`;
    exportToCSV(rows, INVENTORY_EXPORT_COLUMNS, `inventory-${suffix}.csv`);
  }, [cargos, isHistorical, asOfDate]);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>{m.cargos_title()}</CardTitle>
              <CardDescription>
                {m.cargos_description()}
                {cargos && (
                  <span className="ml-2 font-medium">
                    {m.cargos_count({ active: activeCargos.length, archived: archivedCargos.length })}
                  </span>
                )}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  {m.cargos_as_of_label()}
                </span>
                <DatePicker
                  value={asOfDate}
                  onChange={setAsOfDate}
                  placeholder={m.cargos_as_of_live_badge()}
                  maxDate={timestampToLocalDateInputString(Date.now())}
                  className="w-[150px]"
                />
                {isHistorical && (
                  <Button size="sm" variant="ghost" onClick={() => setAsOfDate("")}>
                    {m.cargos_as_of_back_to_live()}
                  </Button>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleExportInventory}
                disabled={!cargos || cargos.length === 0}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                {m.cargos_export_csv()}
              </Button>
              <Button size="sm" onClick={() => setCreateOpen(true)} disabled={isHistorical}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {m.cargo_create()}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isHistorical && (
            <div className="mb-3 space-y-2">
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
                <History className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{m.cargos_as_of_banner({ date: formatDate(asOfMs) })}</span>
              </div>
              {predatesHistory && historyCoverage?.earliestAt != null && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
                  {m.cargos_as_of_pre_history_warning({
                    date: formatDate(historyCoverage.earliestAt),
                  })}
                </div>
              )}
            </div>
          )}
          {cargos === undefined ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <InternalEntityDataTable
              key={isHistorical ? "historical" : "live"}
              data={cargos}
              columns={columns}
              initialSorting={[{ id: "availableBags", desc: true }]}
              initialColumnFilters={
                // In historical mode, status/archived are NOT reconstructed (only
                // quantities are), so the live-status default filter would wrongly
                // hide cargos that are now sold_out/archived. Show all instead.
                isHistorical
                  ? []
                  : [{ id: "status", value: [...DEFAULT_VISIBLE_STATUSES] }]
              }
              actionBar={({ selectedRows, clearSelection }) => (
                <DataTableFloatingBar
                  selectedCount={selectedRows.length}
                  onClear={clearSelection}
                >
                  {selectedRows.length === 1 && (
                    <>
                      {!isHistorical && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditCargo(selectedRows[0]);
                            clearSelection();
                          }}
                        >
                          <Pencil className="mr-1.5 h-3.5 w-3.5" />
                          {m.edit()}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const id = selectedRows[0]._id ?? selectedRows[0].id;
                          window.open(
                            `/cargo-label?id=${id}`,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                      >
                        <Printer className="mr-1.5 h-3.5 w-3.5" />
                        {m.cargos_print_label()}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setAuditCargo(selectedRows[0]);
                          clearSelection();
                        }}
                      >
                        {m.cargos_view_deductions()}
                      </Button>
                    </>
                  )}
                  {!isHistorical && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleBulkArchive(selectedRows, true, clearSelection)}
                      >
                        {m.archive()}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleBulkArchive(selectedRows, false, clearSelection)}
                      >
                        {m.cargos_unarchive()}
                      </Button>
                    </>
                  )}
                </DataTableFloatingBar>
              )}
            />
          )}
        </CardContent>
      </Card>

      {isHistorical && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-base">
              {m.cargos_as_of_changes_title({ date: formatDate(asOfMs) })}
            </CardTitle>
            {changeLog?.truncated && (
              <CardDescription>
                {m.cargos_as_of_truncated({
                  count: changeLog.events.length,
                  total: changeLog.totalInWindow,
                })}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {changeLog === undefined ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : changeLog.events.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {m.cargos_as_of_no_changes()}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{m.cargos_time()}</TableHead>
                      <TableHead>{m.cargos_cargo()}</TableHead>
                      <TableHead>{m.cargos_operation()}</TableHead>
                      <TableHead>{m.cargos_source()}</TableHead>
                      <TableHead>{m.order()}</TableHead>
                      <TableHead>{m.cargos_customer()}</TableHead>
                      <TableHead className="text-right">{m.quantity_short()}</TableHead>
                      <TableHead className="text-right">{m.cargos_avail_before_after()}</TableHead>
                      <TableHead className="text-right">{m.cargos_sold_before_after()}</TableHead>
                      <TableHead className="text-right">{m.cargos_wh_before_after()}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {changeLog.events.map((event: any) => (
                      <TableRow key={event.auditLogId}>
                        <TableCell>{formatAuditDateTime(event.createdAt)}</TableCell>
                        <TableCell className="text-xs">
                          {textOrDash(event.cargoOciNumber || event.productName)}
                        </TableCell>
                        <TableCell>{textOrDash(formatStatusLabel(event.operation, "-"))}</TableCell>
                        <TableCell>{textOrDash(event.source)}</TableCell>
                        <TableCell className="font-mono text-xs">{textOrDash(event.orderNumber)}</TableCell>
                        <TableCell>{textOrDash(event.customerName)}</TableCell>
                        <TableCell className="text-right">{formatBagCount(event.quantityChanged)}</TableCell>
                        <TableCell className="text-right">{`${formatBagCount(event.beforeAvailableBags)} -> ${formatBagCount(event.afterAvailableBags)}`}</TableCell>
                        <TableCell className="text-right">{`${formatBagCount(event.beforeSoldBags)} -> ${formatBagCount(event.afterSoldBags)}`}</TableCell>
                        <TableCell className="text-right">{`${formatBagCount(event.beforeWarehouseBags)} -> ${formatBagCount(event.afterWarehouseBags)}`}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <InventorySalesRangeCard />

      <CargoCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <CargoEditDialog
        open={!!editCargo}
        onOpenChange={(open) => { if (!open) setEditCargo(null); }}
        cargo={editCargo}
        sameProductCargoIds={sameProductCargoIds}
      />
      <CargoOrderInventoryAuditDialog open={!!auditCargo} onOpenChange={(open) => { if (!open) setAuditCargo(null); }} cargo={auditCargo} />
    </>
  );
}
